import { useEffect, useState } from "react";
import { AnimatedBackdrop } from "./components/AnimatedBackdrop";
import { AuthGate } from "./components/AuthGate";
import { GameScreen } from "./screens/GameScreen";
import { MainMenu } from "./screens/MainMenu";
import { ResultScreen } from "./screens/ResultScreen";
import { useGameController } from "./game/hooks/useGameController";
import { initializeAds } from "./services/ads/adService";
import { useAuthState } from "./services/firebase/useAuthState";
import { usePlatformLayout } from "./hooks/usePlatformLayout";
import { loadSettings, normalizeSettings, saveSettings } from "./state/settingsStore";

export default function App() {
	const auth = useAuthState();
	const layoutMode = usePlatformLayout();
	const [settings, setSettingsState] = useState(() => loadSettings());

	const setSettings = (nextSettings) => {
		const normalized = normalizeSettings(nextSettings);
		setSettingsState(normalized);
		saveSettings(normalized);
	};

	const controller = useGameController({
		user: auth.user,
		profile: auth.profile,
		canPlay: auth.configured && Boolean(auth.user),
		loginRequired: true,
		settings,
		onSettingsLoaded: (cloudSettings) => setSettings({ ...settings, ...cloudSettings }),
	});

	useEffect(() => {
		initializeAds().catch((error) => {
			console.warn("Rewarded ads are running in fallback mode.", error);
		});
	}, []);

	useEffect(() => {
		if (!auth.loading && !auth.user && controller.ui.screen !== "menu") {
			controller.actions.backToMenu({
				id: Date.now(),
				message: "Log in to continue defending the colony.",
				tone: "warn",
				createdAt: Date.now(),
			});
		}
	}, [auth.loading, auth.user, controller.actions, controller.ui.screen]);

	return (
		<div className={`app-shell app-shell--${layoutMode}`}>
			<AnimatedBackdrop />
			{controller.ui.screen === "menu" && (
				<MainMenu controller={controller} auth={auth} settings={settings} setSettings={setSettings} />
			)}
			{controller.ui.screen === "playing" && <GameScreen controller={controller} />}
			{["mapComplete", "gameover", "victory"].includes(controller.ui.screen) && <ResultScreen controller={controller} />}
			{(!auth.user || auth.loading) && <AuthGate configured={auth.configured} loading={auth.loading} />}
		</div>
	);
}
